import React, { useState, useEffect } from 'react';
import { AlertCircle, ArrowLeft, X } from 'lucide-react';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (_email: string) => void;
  isLoading: boolean;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>('');

  // Clear email when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    onSubmit(email);
  };

  if (!isOpen) return null;

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
    const error = validateEmail(value);
    if (error) {
      setEmailError(error);
    } else {
      setEmailError('');
    }
  };

  const validateEmail = (email: string): string => {
    if (!email) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return '';
  };

  return (
    <>
      {/* Desktop Forgot Password Modal */}
      {window.innerWidth > 768 ? ( 
        <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 md:flex hidden items-center justify-center z-50 zoomOutLevel">
          <div className="bg-[var(--modal-background)] rounded-xl py-12 px-14 max-w-md w-full mx-4 relative animate-scale-in">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="md:text-3xl text-2xl font-light text-[var(--color-text)] mb-4">Forgot Your Password</h2>
              <p className="text-[var(--color-sub-title-text)] sm:text-sm text-xs text-center">
                Enter your email address and we&apos;ll send you instructions to reset your password.
              </p>
              <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors absolute right-7 top-5"
                >
                  <X className="h-6 w-6" />
              </button>
            </div>

            {/* Email Form */}
            <form noValidate onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="email" className="block text-base font-normal text-[var(--color-sub-title-text)] mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`} alt="Email" className="h-5 w-5 text-[var(--color-sub-title-text)]" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border dark:border-0 rounded-lg focus:border-[#22c55e] dark:bg-[#F5F5F533] bg-[#F5F5F5] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none"
                    placeholder="Enter your email"  
                    required
                  />
                </div>
                {emailError && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !!emailError}
                className="px-10 signin-button"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center mt-6 text-base">
              <p className="text-[var(--color-text)]">
                Remember your password?{' '}
                <button
                  onClick={onClose}
                  className="text-[--color-terms-link] hover:text-[#217c708c] font-bold underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-8 max-w-full w-full">
          {/* Mobile, tablet Forgot Password Modal */}
          {/* Header */}
          <div className="text-center sm:mx-12 mx-4">
            <img src={`${process.env.PUBLIC_URL}/SmallScreenImages/ForgotPassword.svg`} alt="Forgot Password Image" className="w-16 h-16 mx-auto mb-8 object-cover" />
            <h2 className="forgot-password-title sm:text-4xl text-2xl font-light text-[var(--color-text)] mb-4">Forgot Your Password</h2>
            <p className="text-[var(--color-sub-title-text)] sm:text-base text-sm text-center">
              Enter your email address and we&apos;ll send you instructions to reset your password.
            </p>
            <button
                onClick={onClose}
                className="text-[var(--color-text)] transition-colors absolute left-7 top-5"
              >
                <ArrowLeft className="h-6 w-6" />
            </button>
          </div>
          <div className="bg-[var(--modal-background)] rounded-[1.25rem] p-6 w-full mx-6 relative">
            {/* Email Form */}
            <form noValidate onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="email" className="block text-base font-normal text-[var(--color-sub-title-text)] mb-2">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <img src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`} alt="Email" className="h-5 w-5 text-[var(--color-sub-title-text)]" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border dark:border-0 rounded-lg focus:border-[#22c55e] dark:bg-[#F5F5F533] bg-[#F5F5F5] text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] placeholder:text-sm outline-none"
                    placeholder="Enter your email"  
                    required
                  />
                </div>
                {emailError && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !!emailError}
                className="px-10 signin-button"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center mt-6 text-base">
              <p className="text-[var(--color-text)]">
                Remember your password?{' '}
                <button
                  onClick={onClose}
                  className="text-[var(--color-terms-link)] hover:text-[#217c708c] font-bold underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ForgotPasswordModal; 